import { useState, useEffect, useCallback } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, X, FileText } from "lucide-react";
import { trackExitIntent, trackCTAClick } from "../utils/analytics";
import { useBasket } from "../context/BasketContext";

// ═══════════════════════════════════════════════════════════════════════════════
// STICKY CONVERSION BAR - DISABLED
// Chat widget now handles bottom coverage
// ═══════════════════════════════════════════════════════════════════════════════

export function StickyConversionBar() {
  // Disabled per user request - focusing on chat widget coverage instead
  return null;
}

// ═══════════════════════════════════════════════════════════════════════════════
// EXIT INTENT MODAL
// Triggers when user moves mouse toward browser chrome (exit intent)
// Shows once per session
// ═══════════════════════════════════════════════════════════════════════════════

export function ExitIntentModal() {
  const [isOpen, setIsOpen] = useState(false);
  const [hasShown, setHasShown] = useState(false);
  const location = useLocation();
  const { isOpen: basketOpen } = useBasket();

  // Don't show on SmartMatch, Checkout, Command Center pages
  const excludedPaths = ["/smartmatch", "/checkout", "/command-center"];
  const isExcludedPage = excludedPaths.some(path => location.pathname.startsWith(path));

  const handleMouseLeave = useCallback((e) => {
    // Only trigger if mouse leaves toward top of viewport (exit intent)
    // Don't trigger if basket drawer is open
    if (e.clientY <= 10 && !hasShown && !isExcludedPage && !basketOpen) {
      setIsOpen(true);
      setHasShown(true);
      trackExitIntent("shown");
    }
  }, [hasShown, isExcludedPage, basketOpen]);

  useEffect(() => {
    // Check session storage for previous show
    const shown = sessionStorage.getItem("exitIntentShown");
    if (shown) setHasShown(true);
  }, []);

  useEffect(() => {
    if (hasShown || isExcludedPage) return;

    document.addEventListener("mouseleave", handleMouseLeave);
    return () => document.removeEventListener("mouseleave", handleMouseLeave);
  }, [handleMouseLeave, hasShown, isExcludedPage]);

  const handleClose = () => {
    setIsOpen(false);
    sessionStorage.setItem("exitIntentShown", "true");
    trackExitIntent("dismissed");
  };

  const handleConvert = () => {
    trackExitIntent("converted");
    trackCTAClick("Exit Intent CTA", "exit_modal", "/smartmatch");
    handleClose();
  };

  if (isExcludedPage) return null;

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={handleClose}
            className="fixed inset-0 z-[100] bg-black/70"
            data-testid="exit-intent-backdrop"
          />

          {/* Modal */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            transition={{ type: "spring", stiffness: 300, damping: 25 }}
            className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-[101] w-[90vw] max-w-md"
            data-testid="exit-intent-modal"
          >
            <div className="relative bg-gradient-to-br from-slate-900 via-slate-900 to-blue-950/50 rounded-2xl border border-white/10 shadow-2xl shadow-black/50 overflow-hidden">
              {/* Decorative elements */}
              <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-blue-500 via-cyan-400 to-blue-500" />
              <div className="absolute -top-20 -right-20 w-40 h-40 bg-blue-500/10 rounded-full" />

              {/* Close button */}
              <button
                onClick={handleClose}
                className="absolute top-4 right-4 p-2 text-slate-400 hover:text-white transition-colors z-10"
                aria-label="Close modal"
              >
                <X size={20} />
              </button>

              {/* Content */}
              <div className="relative p-8 text-center">
                {/* Icon */}
                <div className="w-16 h-16 mx-auto mb-6 rounded-2xl bg-blue-500/20 border border-blue-400/30 flex items-center justify-center">
                  <FileText size={28} className="text-blue-400" />
                </div>

                {/* Headline */}
                <h2 className="text-2xl font-bold text-white mb-3">
                  Before you go...
                </h2>

                {/* Subheadline */}
                <p className="text-slate-300 text-base leading-relaxed mb-6">
                  Get your personalised strategy report in <span className="text-blue-400 font-semibold">2 minutes</span>.<br />
                  See exactly where your business is losing leads.
                </p>

                {/* Value props */}
                <div className="flex flex-wrap justify-center gap-3 mb-8">
                  {["Free analysis", "No obligation", "Instant results"].map((item, i) => (
                    <span key={i} className="inline-flex items-center gap-1.5 bg-white/5 border border-white/10 text-slate-300 text-xs font-medium px-3 py-1.5 rounded-full">
                      <span className="w-1.5 h-1.5 bg-emerald-400 rounded-full" />
                      {item}
                    </span>
                  ))}
                </div>

                {/* CTA */}
                <Link
                  to="/smartmatch"
                  onClick={handleConvert}
                  data-testid="exit-intent-cta"
                  className="inline-flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-500 text-white font-semibold px-8 py-4 rounded-full text-base transition-all hover:-translate-y-0.5 shadow-lg shadow-blue-600/30 w-full"
                >
                  Get My Free Strategy Report <ArrowRight size={16} />
                </Link>

                {/* Dismiss link */}
                <button
                  onClick={handleClose}
                  className="mt-4 text-sm text-slate-500 hover:text-slate-300 transition-colors"
                >
                  No thanks, I'll figure it out myself
                </button>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// COMBINED EXPORT
// ═══════════════════════════════════════════════════════════════════════════════

export default function ConversionTools() {
  return (
    <>
      <StickyConversionBar />
      <ExitIntentModal />
      <div data-testid="conversion-tools-mounted" style={{display:'none'}} />
    </>
  );
}
